// src/redux/services/rechargePlanApi.js
// ✅ RECHARGE PLAN API (RTK Query)

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BACKEND_URL || "http://localhost:7600/api",
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth?.token || localStorage.getItem("token");
    if (token) headers.set("x-auth-token", token);
    return headers;
  },
});

export const rechargePlanApi = createApi({
  reducerPath: "rechargePlanApi",
  baseQuery,
  tagTypes: ["RechargePlans", "RechargePlansTree"],
  endpoints: (builder) => ({
    // ✅ Visible plans for logged-in user
    getVisibleRechargePlans: builder.query({
      query: () => "/recharge-plans/visible",
      providesTags: (result) =>
        result?.plans
          ? [
              ...result.plans.map((p) => ({ type: "RechargePlans", id: p._id })),
              { type: "RechargePlans", id: "LIST" },
            ]
          : [{ type: "RechargePlans", id: "LIST" }],
      transformResponse: (response) => {
        // Backend may return { plans: [] } OR direct []
        if (Array.isArray(response)) return { plans: response };
        return response?.data || response;
      },
    }),

    // ✅ Plans created by my tree (checking)
    getRechargePlansTree: builder.query({
      query: () => "/recharge-plans/tree",
      providesTags: (result) =>
        result?.plans
          ? [
              ...result.plans.map((p) => ({ type: "RechargePlansTree", id: p._id })),
              { type: "RechargePlansTree", id: "LIST" },
            ]
          : [{ type: "RechargePlansTree", id: "LIST" }],
      transformResponse: (response) => {
        if (Array.isArray(response)) return { plans: response };
        return response?.data || response;
      },
    }),

    // ✅ Create plan
    createRechargePlan: builder.mutation({
      query: (payload) => ({
        url: "/recharge-plans",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [
        { type: "RechargePlans", id: "LIST" },
        { type: "RechargePlansTree", id: "LIST" },
      ],
    }),

    // ✅ Update plan
    updateRechargePlan: builder.mutation({
      query: ({ id, ...payload }) => ({
        url: `/recharge-plans/${id}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: (res, err, arg) => [
        { type: "RechargePlans", id: arg?.id },
        { type: "RechargePlansTree", id: arg?.id },
        { type: "RechargePlans", id: "LIST" },
        { type: "RechargePlansTree", id: "LIST" },
      ],
    }),

    // ✅ Delete plan
    deleteRechargePlan: builder.mutation({
      query: (id) => ({
        url: `/recharge-plans/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "RechargePlans", id: "LIST" },
        { type: "RechargePlansTree", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetVisibleRechargePlansQuery,
  useGetRechargePlansTreeQuery,
  useCreateRechargePlanMutation,
  useUpdateRechargePlanMutation,
  useDeleteRechargePlanMutation,
} = rechargePlanApi;
